(() => {
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const setExpanded = (button, expanded) => {
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const initHeader = (scope = document) => {
    scope.querySelectorAll('[data-vc-header]').forEach((header) => {
      if (header.dataset.vcReady === 'true') return;
      header.dataset.vcReady = 'true';

      const menu = header.querySelector('[data-vc-mobile-menu]');
      const menuToggle = header.querySelector('[data-vc-menu-toggle]');
      const menuClose = header.querySelector('[data-vc-menu-close]');
      const menuScrim = header.querySelector('[data-vc-menu-scrim]');
      const searchPanel = header.querySelector('[data-vc-search-panel]');
      const searchToggle = header.querySelector('[data-vc-search-toggle]');
      let lastFocus = null;

      const closeMenu = (restoreFocus = true) => {
        if (!menu || menu.hidden) return;
        menu.hidden = true;
        if (menuScrim) menuScrim.hidden = true;
        if (menuToggle) setExpanded(menuToggle, false);
        document.documentElement.classList.remove('vc-menu-open');
        if (restoreFocus && lastFocus) lastFocus.focus();
      };

      const openMenu = () => {
        if (!menu) return;
        lastFocus = document.activeElement;
        menu.hidden = false;
        if (menuScrim) menuScrim.hidden = false;
        if (menuToggle) setExpanded(menuToggle, true);
        document.documentElement.classList.add('vc-menu-open');
        const firstFocusable = menu.querySelector(focusableSelector);
        if (firstFocusable) firstFocusable.focus();
      };

      if (menuToggle) menuToggle.addEventListener('click', () => (menu && menu.hidden ? openMenu() : closeMenu()));
      if (menuClose) menuClose.addEventListener('click', () => closeMenu());
      if (menuScrim) menuScrim.addEventListener('click', () => closeMenu());

      if (searchToggle && searchPanel) {
        searchToggle.addEventListener('click', () => {
          const shouldOpen = searchPanel.hidden;
          searchPanel.hidden = !shouldOpen;
          setExpanded(searchToggle, shouldOpen);
          if (shouldOpen) searchPanel.querySelector('input[type="search"]')?.focus();
        });
      }

      header.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closeMenu();
          if (searchToggle && searchPanel && !searchPanel.hidden) {
            searchPanel.hidden = true;
            setExpanded(searchToggle, false);
            searchToggle.focus();
          }
        }

        if (event.key === 'Tab' && menu && !menu.hidden) {
          const focusable = [...menu.querySelectorAll(focusableSelector)];
          if (!focusable.length) return;
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
      });

      const desktopMedia = window.matchMedia('(min-width: 1281px)');
      desktopMedia.addEventListener?.('change', (event) => {
        if (event.matches) closeMenu(false);
      });
    });
  };

  const initLiveSearch = (scope = document) => {
    scope.querySelectorAll('[data-vc-live-search]').forEach((form) => {
      if (form.dataset.vcSearchReady === 'true') return;
      form.dataset.vcSearchReady = 'true';
      const input = form.querySelector('input[type="search"]');
      const results = form.querySelector('[data-vc-live-search-results]');
      if (!input || !results) return;
      let timer = null;
      let requestId = 0;

      const close = (clear = false) => {
        results.hidden = true;
        results.removeAttribute('aria-busy');
        input.setAttribute('aria-expanded', 'false');
        if (clear) results.innerHTML = '';
      };

      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        const currentRequest = ++requestId;
        const query = input.value.trim();
        if (query.length < 2) {
          close(true);
          return;
        }
        results.setAttribute('aria-busy', 'true');
        timer = window.setTimeout(() => {
          const endpoint = window.routes?.predictive_search_url || '/search/suggest';
          fetch(`${endpoint}?q=${encodeURIComponent(query)}&resources%5Btype%5D=product&resources%5Blimit%5D=6&section_id=predictive-search`)
            .then((response) => response.ok ? response.text() : Promise.reject(new Error('Search unavailable')))
            .then((html) => {
              if (currentRequest !== requestId || input.value.trim() !== query) return;
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const productResults = parsed.querySelector('#predictive-search-product');
              const resultItems = productResults?.querySelectorAll('.predictive-search__list-item');
              if (!productResults || !resultItems?.length || !productResults.textContent.trim()) {
                close(true);
                return;
              }
              results.innerHTML = productResults.innerHTML.trim();
              results.removeAttribute('aria-busy');
              results.hidden = false;
              input.setAttribute('aria-expanded', 'true');
            })
            .catch(() => {
              if (currentRequest === requestId) close(true);
            });
        }, 220);
      });

      input.addEventListener('focus', () => {
        if (input.value.trim().length >= 2 && results.querySelector('.predictive-search__list-item')) {
          results.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        }
      });

      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });
      form.addEventListener('submit', () => close());
      form.addEventListener('focusout', () => window.setTimeout(() => {
        if (!form.contains(document.activeElement)) close();
      }, 120));
    });
  };

  const initTabs = (scope = document) => {
    scope.querySelectorAll('[data-vc-tabs]').forEach((tabs) => {
      if (tabs.dataset.vcReady === 'true') return;
      tabs.dataset.vcReady = 'true';
      const buttons = [...tabs.querySelectorAll('[data-vc-tab]')];
      const panels = [...tabs.querySelectorAll('[data-vc-panel]')];

      const activate = (index, focus = false) => {
        buttons.forEach((button, buttonIndex) => {
          const active = buttonIndex === index;
          button.setAttribute('aria-selected', active ? 'true' : 'false');
          button.tabIndex = active ? 0 : -1;
          if (active && focus) button.focus();
        });
        panels.forEach((panel, panelIndex) => {
          panel.hidden = panelIndex !== index;
        });
      };

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(index));
        button.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          let next = index;
          if (event.key === 'ArrowLeft') next = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'ArrowRight') next = (index + 1) % buttons.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = buttons.length - 1;
          activate(next, true);
        });
      });
    });
  };

  const initFaq = (scope = document) => {
    scope.querySelectorAll('.vc-faq details').forEach((details) => {
      if (details.dataset.vcReady === 'true') return;
      details.dataset.vcReady = 'true';
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        details.closest('.vc-faq')?.querySelectorAll('details[open]').forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
  };

  const initFooterMenus = (scope = document) => {
    scope.querySelectorAll('[data-vc-footer-menu]').forEach((menu) => {
      if (menu.dataset.vcFooterReady === 'true') return;
      menu.dataset.vcFooterReady = 'true';
      const mobileMedia = window.matchMedia('(max-width: 820px)');
      const sync = (isMobile) => {
        if (isMobile) {
          if (!menu.dataset.vcMobileInitialised) {
            menu.open = false;
            menu.dataset.vcMobileInitialised = 'true';
          }
        } else {
          menu.open = true;
        }
      };
      sync(mobileMedia.matches);
      mobileMedia.addEventListener?.('change', (event) => sync(event.matches));
    });
  };

  const initCollection = (scope = document) => {
    scope.querySelectorAll('[data-vc-collection]').forEach((collection) => {
      if (collection.dataset.vcReady === 'true') return;
      collection.dataset.vcReady = 'true';
      const preferredSort = collection.dataset.vcDefaultSort;
      if (preferredSort) {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('sort_by')) {
          url.searchParams.set('sort_by', preferredSort);
          window.location.replace(url.toString());
          return;
        }
      }
      const drawer = collection.querySelector('[data-vc-filter-drawer]');
      const scrim = collection.querySelector('[data-vc-filter-scrim]');
      const openButton = collection.querySelector('[data-vc-filter-open]');
      const closeButton = collection.querySelector('[data-vc-filter-close]');
      let lastFocus = null;

      const close = () => {
        if (!drawer) return;
        drawer.hidden = true;
        if (scrim) scrim.hidden = true;
        if (openButton) setExpanded(openButton, false);
        document.documentElement.classList.remove('vc-filters-open');
        lastFocus?.focus();
      };
      const open = () => {
        if (!drawer) return;
        lastFocus = document.activeElement;
        drawer.hidden = false;
        if (scrim) scrim.hidden = false;
        if (openButton) setExpanded(openButton, true);
        document.documentElement.classList.add('vc-filters-open');
        drawer.querySelector(focusableSelector)?.focus();
      };

      openButton?.addEventListener('click', open);
      closeButton?.addEventListener('click', close);
      scrim?.addEventListener('click', close);
      drawer?.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') close();
      });

      collection.querySelectorAll('[data-vc-sort]').forEach((select) => {
        select.addEventListener('change', () => select.form?.submit());
      });

      collection.querySelectorAll('[data-vc-facet-search]').forEach((input) => {
        const list = input.closest('.vc-facet__body')?.querySelector('[data-vc-facet-list]');
        if (!list) return;
        input.addEventListener('input', () => {
          const query = input.value.trim().toLowerCase();
          list.querySelectorAll('li').forEach((item) => {
            item.hidden = query && !item.textContent.toLowerCase().includes(query);
          });
        });
      });

      collection.querySelectorAll('[data-vc-facet-expand]').forEach((button) => {
        const list = button.previousElementSibling;
        if (!list) return;
        button.addEventListener('click', () => {
          const expanded = list.classList.toggle('is-expanded');
          button.textContent = expanded ? 'Show fewer' : 'Show all';
          button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
      });
    });
  };

  const formatMoney = (cents) => {
    if (window.Shopify?.formatMoney && window.wpbingo?.strings?.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.wpbingo.strings.moneyFormat);
    }
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: window.routes?.shop_currency || 'GBP'
    }).format(cents / 100);
  };

  const initProducts = (scope = document) => {
    scope.querySelectorAll('[data-vc-product]').forEach((productRoot) => {
      if (productRoot.dataset.vcReady === 'true') return;
      productRoot.dataset.vcReady = 'true';
      const productData = productRoot.querySelector('[data-vc-product-json]');
      const variants = productData ? JSON.parse(productData.textContent) : [];
      const inventoryData = productRoot.querySelector('[data-vc-inventory-json]');
      const inventory = inventoryData ? JSON.parse(inventoryData.textContent) : [];
      const inventoryById = new Map(inventory.map((item) => [String(item.id), item]));
      const lowStockThreshold = Number(productRoot.dataset.vcLowStockThreshold || 1);
      const optionInputs = [...productRoot.querySelectorAll('[data-vc-option]')];
      const variantInput = productRoot.querySelector('[data-vc-variant-id]');
      const price = productRoot.querySelector('[data-vc-product-price]');
      const priceCurrent = price?.querySelector('strong');
      const priceCompare = price?.querySelector('[data-vc-product-compare]');
      const priceSaving = price?.querySelector('[data-vc-product-saving]');
      const saleBadge = productRoot.querySelector('[data-vc-sale-badge]');
      const productInSaleCollection = productRoot.dataset.vcSaleCollection === 'true';
      const productHasPromoBadge = productRoot.dataset.vcPromoBadge === 'true';
      const productIsPreorder = productRoot.dataset.vcPreorder === 'true';
      const hasMultipleVariants = productRoot.dataset.vcHasMultipleVariants === 'true';
      const stock = productRoot.querySelector('[data-vc-stock]');
      const submit = productRoot.querySelector('[data-vc-add-to-cart]');
      const quantity = productRoot.querySelector('[data-vc-quantity]');
      const storeAvailability = productRoot.querySelector('[data-vc-store-availability]');
      let availabilityRequest = 0;
      let variantSelectionMade = false;

      const applyColourFallbacks = () => {
        const colourMap = [
          [['black'], '#1d2024'],
          [['white', 'ivory', 'pearly'], '#f4f2ec'],
          [['navy'], '#243755'],
          [['blue', 'aurora', 'abyssal'], '#3476b8'],
          [['teal', 'turquoise'], '#16878a'],
          [['green'], '#40835d'],
          [['purple', 'violet'], '#7653a2'],
          [['pink', 'rose'], '#d983a4'],
          [['red', 'crimson'], '#bd3148'],
          [['orange'], '#df7a32'],
          [['yellow'], '#dec74c'],
          [['gold'], '#c4a052'],
          [['silver', 'grey', 'gray', 'gunmetal', 'slate', 'cloud', 'carbon'], '#939aa2'],
          [['brown', 'bronze'], '#80614c']
        ];

        productRoot.querySelectorAll('[data-vc-swatch]').forEach((swatch) => {
          const visual = swatch.querySelector('.vc-swatch__visual');
          if (!visual) return;
          const colourName = String(swatch.dataset.vcSwatchValue || '').trim().toLowerCase();
          const explicitHex = colourName.match(/#[0-9a-f]{3,8}\b/i)?.[0];
          const declaredFallback = String(visual.dataset.vcFallbackColour || '').trim();
          const matchedColour = declaredFallback || explicitHex || colourMap.find(([names]) => names.some((name) => colourName.includes(name)))?.[1];
          const currentColour = String(visual.style.backgroundColor || '').replace(/\s+/g, '').toLowerCase();
          const colourIsMissing = !currentColour || currentColour === 'transparent' || /rgba\([^)]*,0(?:\.0+)?\)$/.test(currentColour);
          if (matchedColour && colourIsMissing) {
            visual.style.backgroundColor = matchedColour;
            visual.classList.add('is-colour-fallback');
          }
        });
      };

      const getSelectedOptions = () => optionInputs.map((input) => input.value);

      const updateOptionAvailability = () => {
        const selectedOptions = getSelectedOptions();
        optionInputs.forEach((input, optionIndex) => {
          [...input.options].forEach((option) => {
            const available = variants.some((variant) => variant.available && variant.options.every((value, index) => {
              if (index === optionIndex) return value === option.value;
              return value === selectedOptions[index];
            }));
            option.disabled = !available;
            const originalLabel = option.dataset.vcOriginalLabel || option.textContent.replace(/\s+—\s+Sold out$/, '');
            option.dataset.vcOriginalLabel = originalLabel;
            option.textContent = available ? originalLabel : `${originalLabel} (sold out)`;
            const swatch = productRoot.querySelector(`[data-vc-swatch][data-vc-option-position="${optionIndex + 1}"][data-vc-swatch-value="${CSS.escape(option.value)}"]`);
            if (swatch) {
              swatch.classList.toggle('is-unavailable', !available);
              swatch.setAttribute('aria-disabled', available ? 'false' : 'true');
              swatch.title = available ? option.value : `${option.value} (sold out)`;
            }
          });
        });
      };

      const updateStoreAvailability = (variantId) => {
        if (!storeAvailability || !variantId) return;
        const requestId = ++availabilityRequest;
        storeAvailability.setAttribute('aria-busy', 'true');
        const rootUrl = window.Shopify?.routes?.root || '/';
        fetch(`${rootUrl}variants/${variantId}/?section_id=vc-store-availability`)
          .then((response) => response.text())
          .then((html) => {
            if (requestId !== availabilityRequest) return;
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const response = parsed.querySelector('[data-vc-store-availability-response]');
            const content = response?.innerHTML.trim() || '';
            storeAvailability.innerHTML = content;
            storeAvailability.hidden = !content;
          })
          .catch(() => {
            if (requestId !== availabilityRequest) return;
            storeAvailability.innerHTML = '';
            storeAvailability.hidden = true;
          })
          .finally(() => {
            if (requestId === availabilityRequest) storeAvailability.removeAttribute('aria-busy');
          });
      };

      const showMedia = (mediaId) => {
        if (!mediaId) return;
        productRoot.querySelectorAll('[data-vc-product-media]').forEach((media) => {
          const active = String(media.dataset.vcProductMedia) === String(mediaId);
          media.hidden = !active;
          media.classList.toggle('is-active', active);
        });
        productRoot.querySelectorAll('[data-vc-product-thumb]').forEach((thumb) => {
          const active = String(thumb.dataset.vcProductThumb) === String(mediaId);
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      };

      productRoot.querySelectorAll('[data-vc-product-thumb]').forEach((thumb) => {
        thumb.addEventListener('click', () => showMedia(thumb.dataset.vcProductThumb));
      });

      const updateVariant = () => {
        const selectedOptions = getSelectedOptions();
        updateOptionAvailability();
        const variant = variants.find((candidate) => candidate.options.every((option, index) => option === selectedOptions[index]));
        if (!variant) {
          if (submit) {
            submit.disabled = true;
            submit.querySelector('span').textContent = 'Unavailable';
          }
          if (stock) {
            stock.classList.add('is-out');
            stock.classList.remove('is-preorder');
            stock.lastChild.textContent = ' This combination is unavailable';
          }
          return;
        }

        if (variantInput) variantInput.value = variant.id;
        if (priceCurrent) priceCurrent.innerHTML = formatMoney(variant.price);
        if (priceCompare) {
          const showCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
          const showSale = showCompare && productInSaleCollection;
          priceCompare.hidden = !showCompare;
          priceCompare.classList.toggle('is-sale', showSale);
          priceCompare.innerHTML = showCompare ? (showSale ? `<s>${formatMoney(variant.compare_at_price)}</s>` : `RRP ${formatMoney(variant.compare_at_price)}`) : '';
          if (priceSaving) {
            const saving = showSale ? variant.compare_at_price - variant.price : 0;
            const percentage = showSale ? Math.round((saving / variant.compare_at_price) * 100) : 0;
            priceSaving.hidden = !showSale;
            priceSaving.textContent = showSale ? `Save ${formatMoney(saving)} (${percentage}%)` : '';
          }
          if (saleBadge) {
            saleBadge.hidden = !showSale || productHasPromoBadge || productIsPreorder;
            saleBadge.textContent = showSale && !productHasPromoBadge && !productIsPreorder ? 'Sale' : '';
          }
        }
        if (stock) {
          const inventoryState = inventoryById.get(String(variant.id));
          const isLow = variant.available && !productIsPreorder && (!hasMultipleVariants || variantSelectionMade) && inventoryState?.management === 'shopify' && inventoryState.policy === 'deny' && inventoryState.quantity > 0 && inventoryState.quantity <= lowStockThreshold;
          const isBackorder = variant.available && !productIsPreorder && inventoryState?.management === 'shopify' && inventoryState.policy === 'continue' && inventoryState.quantity <= 0;
          stock.classList.toggle('is-out', !variant.available);
          stock.classList.toggle('is-preorder', productIsPreorder && variant.available);
          stock.classList.toggle('is-low', isLow);
          stock.classList.toggle('is-backorder', isBackorder);
          let stockMessage = ' In stock and ready to order';
          if (!variant.available) stockMessage = productIsPreorder ? ' Pre-order currently unavailable' : ' Sold out';
          else if (productIsPreorder) stockMessage = ' Available to pre-order';
          else if (isLow) stockMessage = ` Only ${inventoryState.quantity} left online`;
          else if (isBackorder) stockMessage = ' Available to order';
          stock.lastChild.textContent = stockMessage;
        }
        if (submit) {
          submit.disabled = !variant.available;
          submit.querySelector('span').textContent = variant.available ? (productIsPreorder ? 'Pre-order now' : 'Add to basket') : 'Sold out';
        }
        if (variant.featured_media?.id) showMedia(variant.featured_media.id);
        updateStoreAvailability(variant.id);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      };

      applyColourFallbacks();
      optionInputs.forEach((input) => input.addEventListener('change', () => {
        variantSelectionMade = true;
        updateVariant();
      }));
      productRoot.querySelectorAll('[data-vc-swatch]').forEach((swatch) => {
        swatch.addEventListener('click', () => {
          if (swatch.getAttribute('aria-disabled') === 'true') return;
          const select = optionInputs.find((input) => String(input.dataset.vcOption) === String(swatch.dataset.vcOptionPosition));
          if (!select) return;
          select.value = swatch.dataset.vcSwatchValue;
          productRoot.querySelectorAll(`[data-vc-swatch][data-vc-option-position="${swatch.dataset.vcOptionPosition}"]`).forEach((button) => {
            const selected = button === swatch;
            button.classList.toggle('is-selected', selected);
            button.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });
          const selectedLabel = productRoot.querySelector(`[data-vc-swatch-selection="${swatch.dataset.vcOptionPosition}"]`);
          if (selectedLabel) selectedLabel.textContent = swatch.dataset.vcSwatchValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      updateOptionAvailability();
      productRoot.querySelector('[data-vc-quantity-minus]')?.addEventListener('click', () => {
        if (!quantity) return;
        quantity.value = Math.max(Number(quantity.min || 1), Number(quantity.value || 1) - 1);
      });
      productRoot.querySelector('[data-vc-quantity-plus]')?.addEventListener('click', () => {
        if (!quantity) return;
        quantity.value = Number(quantity.value || 1) + 1;
      });
      if (variantInput?.value) updateStoreAvailability(variantInput.value);
    });
  };

  const initRecommendations = (scope = document) => {
    scope.querySelectorAll('[data-vc-recommendations]').forEach((section) => {
      if (section.dataset.vcReady === 'true' || section.innerHTML.trim()) return;
      section.dataset.vcReady = 'true';
      fetch(section.dataset.url)
        .then((response) => response.text())
        .then((html) => {
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const replacement = documentFragment.querySelector('[data-vc-recommendations]');
          if (replacement?.innerHTML.trim()) section.innerHTML = replacement.innerHTML;
        })
        .catch(() => {});
    });
  };

  const initRecentlyViewed = (scope = document) => {
    scope.querySelectorAll('[data-vc-recently-viewed]').forEach((section) => {
      if (section.dataset.vcReady === 'true') return;
      section.dataset.vcReady = 'true';
      const currentHandle = section.dataset.currentHandle;
      const limit = Number(section.dataset.limit || 4);
      const grid = section.querySelector('[data-vc-recently-viewed-grid]');
      const storageKey = 'vcRecentlyViewed';
      let storedHandles = [];
      try {
        storedHandles = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
        if (!Array.isArray(storedHandles)) storedHandles = [];
      } catch (_) {
        storedHandles = [];
      }

      const handlesToShow = storedHandles.filter((handle) => handle && handle !== currentHandle).slice(0, limit);
      const nextHandles = [currentHandle, ...storedHandles.filter((handle) => handle && handle !== currentHandle)].slice(0, 12);
      try { window.localStorage.setItem(storageKey, JSON.stringify(nextHandles)); } catch (_) {}
      if (!grid || !handlesToShow.length) return;

      const rootUrl = window.Shopify?.routes?.root || '/';
      Promise.all(handlesToShow.map((handle) => fetch(`${rootUrl}products/${encodeURIComponent(handle)}?view=vc-card`)
        .then((response) => response.ok ? response.text() : '')
        .then((html) => {
          if (!html) return '';
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          return parsed.querySelector('.vc-recent-product-fragment')?.innerHTML || '';
        })
        .catch(() => '')))
        .then((cards) => {
          const markup = cards.filter(Boolean).join('');
          if (!markup) return;
          grid.innerHTML = markup;
          section.hidden = false;
        });
    });
  };

  const init = (scope = document) => {
    initHeader(scope);
    initLiveSearch(scope);
    initTabs(scope);
    initFaq(scope);
    initFooterMenus(scope);
    initCollection(scope);
    initProducts(scope);
    initRecommendations(scope);
    initRecentlyViewed(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
